import store from '@ps/store'
import _ from 'lodash'
// @ts-ignore
import qc from 'qc'
import { findByUuid, getDescendants } from '@ps/bridge/qc/node/utils'
import {
  getNodesLayout,
  hadChildren,
  isParticle,
  // isDrawLinePathLineNode,
  isCarouselNode,
  isCarouselGroup,
  isEraseNode,
  isParkNode,
  isMaskComponentNode,
  isPuzzleNode,
} from '@ps/bridge/qc/base'
import gameIframe from '@ps/bridge/iframe/iframe'
import { Event, EventBus } from '@ps/utils/event-bus'
import { rad2deg } from '@com/utils/unit-converters/angle'
import nodePool from '@ps/bridge/qc/node-pool'
import { updatePositionTween } from '@ps/workbench/animation'
import { getScaleShapeDelta, moveNodeByWorldPosition } from '@ps/bridge/qc/node'
import { isDragonBone } from '@ps/utils/game'
import { calcWorldPositionByLayoutData } from '@ps/bridge/qc/scripts/layout'
import { updateAnotherResolutionLayout } from '@ps/store/workbench/common/layout'
import { isShelvesNode } from '@ps/workbench/undo-redo/module/shelves'

/**
 * @param nodes {{}[]} 节点新样式
 * @param root 根节点
 * @param 找到节点回调后的回调函数
 */
export function nodesForEach({
  nodes,
  root,
  callback,
}: {
  nodes: {
    uuid: string
    [key: string]: number | string | boolean
  }[]
  root: qc.Node
  callback: ({
    key,
    value,
    node,
  }: {
    key: string
    value: number | string | boolean
    node: qc.Node
  }) => void
}) {
  const uuids = nodes.map((node) => node.uuid)
  const qcNodes: qc.Node[] = []
  uuids.forEach((uuid) => {
    const node = nodePool.find(uuid)
    node && qcNodes.push(nodePool.find(uuid))
  })

  _.each(nodes, (node) => {
    const qcNode = qcNodes.find((item) => item.uuid === node.uuid)
    // 过滤uuid属性，其他的是样式数据
    qcNode &&
      _.each(node, (val, attr) => {
        callback({ key: attr, value: val, node: qcNode })
      })
  })
}

/**
 * 获取节点旧的样式，undo 的时候使用
 * @param nodes {[]{}}
 * @param qcNodes
 * @returns {[]{}}
 */
export const getNodesOldStyle = ({
  nodes,
  qcNodes,
}: {
  qcNodes: qc.Node[]
  nodes: {
    uuid: string
    [key: string]: number | string | boolean // 暂时这么写
  }[]
}) => {
  const cloneNodes = _.cloneDeep(nodes)
  _.each(cloneNodes, (node) => {
    const qcNode = qcNodes.find((item) => item.uuid === node.uuid)
    qcNode &&
      _.each(node, (val, attr) => {
        // @ts-ignore
        node[attr] = qcNode[attr]
      })
  })
  return cloneNodes
}

/**
 * 更新节点列表的样式，这里必须保证是同步的
 * 如果业务有异步的场景，将异步逻辑提取出去
 */
export const updateNodesStyle = ({
  nodes,
}: {
  nodes: {
    uuid: string
    [key: string]: number | string | boolean // 暂时这么写
  }[]
}): void => {
  nodesForEach({
    // 保证修改节点为画布中有效节点
    nodes: nodes.filter((item) => {
      const temp = nodePool.find(item.uuid)
      return temp && temp.parent
    }),
    root: gameIframe.UIRoot,
    callback: ({ key, value, node }) => {
      if (key === 'uuid') return

      // 如果节点id与 activeNode id一样，则使用 activeNode 去更新，保证能够联动到 mirrorNode
      const activeNode = store.state.workspace.game.activeNode
      if (activeNode && node.uuid === activeNode.uuid) node = activeNode

      // 必须通过 commit 去设置样式，因为里面处理了很多逻辑
      store.commit(`workspace/game/set${_.upperFirst(key)}`, {
        value,
        node,
      })
      EventBus.$emit(Event.NODE_STYLE_UPDATE, node, key)
    },
  })
}

/**
 * 批量更换节点父节点
 * @param parent
 * @param nodes
 * @param index
 */
export const switchParent = ({
  parent,
  nodes,
  index,
}: {
  parent: qc.Node
  nodes: qc.Node[]
  index: number
}) => {
  const qcTarget = findByUuid(parent.uuid) as qc.Node
  // 获取源节点
  nodes = nodes.map((node) => findByUuid(node.uuid)) as qc.Node[]

  // 获取迁移之前另外屏幕的世界坐标
  const anotherResolution = store.state.workspace.game.device.anotherResolution

  // 可能触发layout改动的关联节点，（要在移动节点之前获取相关父节点）
  const relatedNodes = [qcTarget].concat(nodes).map((node) => node.parent)
  nodes.forEach((node, j) => {
    // 获取节点在迁移父节点之前的世界坐标
    const beforeWorldPosition = calcWorldPositionByLayoutData(
      node,
      anotherResolution,
    )
    // 节点变换前位于目标节点排序
    const nodeIndexBeforeSwitch = qcTarget.getChildIndex(node)
    // reCalLayoutData(node, qcTarget)
    // 移动节点存在于目标节点前，目标节点 - 1
    if (nodeIndexBeforeSwitch >= 0 && index > nodeIndexBeforeSwitch) {
      node.switchParent(qcTarget, index - 1)
    } else {
      node.switchParent(qcTarget, index)
    }
    // 为啥要定时器执行，因为 switchParent 的时候，可能新的 parent 的 layout 还没有准备好
    // 如果 parent 的宽高不准确，会直接影响到最终计算出来的 layoutData
    // 由于另外一个屏幕的 layoutData 并不需要实时去计算，所以延迟 300ms 后再计算
    setTimeout(() => {
      // 获取迁移的父节点在另外一个分辨率的世界坐标
      const parentWorldPosition = calcWorldPositionByLayoutData(
        node.parent,
        anotherResolution,
      )
      const worldScale = gameIframe.UIRoot.getWorldScale()
      // 知道历史的位置，并且知道父节点的世界坐标，推算出节点相对于父节点的坐标值
      const localPos = {
        x: (beforeWorldPosition.x - parentWorldPosition.x) / worldScale.x,
        y: (beforeWorldPosition.y - parentWorldPosition.y) / worldScale.y,
      }
      updateAnotherResolutionLayout({
        node,
        anotherResolution,
        localPos,
      })
    }, 300)

    if (nodeIndexBeforeSwitch >= 0 && index > nodeIndexBeforeSwitch) {
      index = index - 1
    }
    updateNodesStyle({
      nodes: [
        {
          uuid: node.uuid,
          x: node.x,
          y: node.y,
        },
      ],
    })
  })

  return {
    relatedNodes,
  }
}

/**
 * @param group 组节点
 * @param nodes 计算组节点layout相关nodes
 * @returns
 */
export const resetGroupLayoutByNodes = ({
  group,
  nodes,
}: {
  group: qc.Node
  nodes: qc.Node[]
}) => {
  if (!nodes || !group) return

  nodes = _.unionBy(nodes, 'uuid')

  if (nodes.length <= 0) return

  const currentSceneNode = store.state.workspace.sceneManager.currentSceneNode
  const tempRotation = group.rotation !== 0 ? group.rotation : 0
  // const getRotePosition = (p1: {
  //   x: number;
  //   y: number;
  // }, p2: {
  //   x: number;
  //   y: number;
  // }, deg:number): {
  //   x: number;
  //   y: number;
  // } => {
  //   deg = rad2deg(deg)
  //   const rad = deg2rad(360 - deg)
  //   return {
  //     x: (p1.x - p2.x) * Math.cos(rad) - (p1.y - p2.y) * Math.sin(rad) + p2.x,
  //     y: (p1.x - p2.x) * Math.sin(rad) + (p1.y - p2.y) * Math.cos(rad) + p2.y
  //   }
  // }

  // let tempRotationPoint: {
  //   x: number;
  //   y: number;
  // } | any = {}
  if (tempRotation !== 0) {
    updateNodesStyle({
      nodes: [
        {
          uuid: group.uuid,
          rotation: 0,
        },
      ],
    })
    // tempRotationPoint = {
    //   x: group.x + group.width / 2,
    //   y: group.y - group.height / 2
    // }
  }
  const layout = getNodesLayout(group, nodes, currentSceneNode)
  const data = {
    uuid: group.uuid,
    ...layout,
  }
  // if (tempRotation !== 0) {
  //   const p = _.pick(getRotePosition({ ..._.pick(layout, ['x', 'y']) }, tempRotationPoint, tempRotation), ['x', 'y'])
  //   data.x = p.x
  //   data.y = p.y
  // }

  const offsetX = data.x - group.x
  const offsetY = data.y - group.y

  updateNodesStyle({
    nodes: [data],
  })

  if (tempRotation !== 0) {
    updateNodesStyle({
      nodes: [
        {
          uuid: group.uuid,
          rotation: Number.parseInt(rad2deg(tempRotation).toFixed(0), 10),
        },
      ],
    })
  }

  if (group.children.length) {
    group.children.forEach((node: any) => {
      updateNodesStyle({
        nodes: [
          {
            uuid: node.uuid,
            x: node.x - offsetX,
            y: node.y - offsetY,
          },
        ],
      })
    })
  }
}
/**
 * 批量更换节点父节点，更换完父节点后刷图层树
 * @param parent
 * @param nodes
 * @param index
 */
export const switchParentAndRefresh = ({
  parent,
  nodes,
  index,
}: {
  parent: qc.Node
  nodes: qc.Node[]
  index: number
}) => {
  const { relatedNodes } = switchParent({
    parent,
    nodes,
    index,
  })

  // 切换父节点，更新位移动画坐标
  nodes.forEach((node) => {
    updatePositionTween(node)
  })

  EventBus.$emit(Event.REFRESHLAYER)
}

/**
 * 设置样式
 * @param node
 * @param style
 */
export function setStyle(node: qc.Node, style: Record<string, any>): void {
  _.each(style, (value, key) => {
    if (key === 'uuid') return

    // 如果节点id与 activeNode id一样，则使用 activeNode 去更新，保证能够联动到 mirrorNode
    const activeNode = store.state.workspace.game.activeNode
    if (activeNode && node.uuid === activeNode.uuid) node = activeNode

    // 必须通过 commit，因为里面处理了很多逻辑
    store.commit(`workspace/game/set${_.upperFirst(key)}`, {
      value,
      node,
    })
  })
}

export type LayoutStyle = {
  height: number
  width: number
  uuid: string
  // 相对于父节点的 x
  x: number
  // 相对于父节点的 y
  y: number
  scaleX: number
  scaleY: number
}

// 组节点更新缩放是否需要联动子孙元素
// 如果节点的 scaleX/Y 为 0 的时候，里面所有元素都会紧凑在一起，如果更新子孙节点的话，会导致子孙节点的 x/y 都为 0。因此当 scaleX/Y 为 0 时，不需要更新子孙节点
export const AllowGroupLinkage = (node: qc.Node): boolean => {
  return !(
    isParticle(node) ||
    isDragonBone(node) ||
    isEraseNode(node) ||
    isCarouselNode(node) ||
    isCarouselGroup(node) ||
    isParkNode(node) ||
    isMaskComponentNode(node) ||
    isPuzzleNode(node) ||
    isShelvesNode(node) ||
    node.scaleX === 0 ||
    node.scaleY === 0 ||
    node.getWorldScale().x === 0 ||
    node.getWorldScale().y === 0
  )
}
// 判断当前节点可视化操作缩放是否是调整缩放值
export const isTransformByScale = (node: qc.Node): boolean => {
  return (
    isDragonBone(node) ||
    isParkNode(node) ||
    isPuzzleNode(node) ||
    isShelvesNode(node)
  )
}

/**
 * 根据节点的宽高，计算出节点与其后代节点的布局数据
 * @param node
 * @param style
 */
export function getLayoutStyleBySize(
  node: qc.Node,
  style: {
    width?: number
    height?: number
    x?: number
    y?: number
    scaleX?: number
    scaleY?: number
  },
): {
  style: LayoutStyle[]
  oldStyle: LayoutStyle[]
} {
  // 判断当前节点是否是缩放时改动缩放值
  const nodeChangeByScale = isTransformByScale(node)
  // 记录修改前后状态值，保证历史回滚正常
  const preNodes = [
    {
      height: node.height,
      width: node.width,
      uuid: node.uuid,
      x: node.x,
      y: node.y,
      scaleX: node.scaleX,
      scaleY: node.scaleY,
    },
  ]
  let valueHeightScale = 1
  let valueWidthScale = 1
  if (style.width) {
    valueWidthScale = style.width / node.width
  }
  if (style.height) {
    valueHeightScale = style.height / node.height
  }
  const nodeScaleX = style.scaleX || node.scaleX
  const nodeScaleY = style.scaleY || node.scaleY
  const nodesStyle = [
    {
      height: nodeChangeByScale ? node.height : style.height,
      width: nodeChangeByScale ? node.width : style.width,
      uuid: node.uuid,
      x: style.x || node.x,
      y: style.y || node.y,
      scaleX: nodeChangeByScale ? nodeScaleX * valueWidthScale : nodeScaleX,
      scaleY: nodeChangeByScale ? nodeScaleY * valueHeightScale : nodeScaleY,
    },
  ]
  const preBox = {
    originX: node.getWorldPosition().x,
    originY: node.getWorldPosition().y,
  }
  // 当操作的是组节点，需要同步修改组内的所有子元素
  if (hadChildren(node) && AllowGroupLinkage(node)) {
    const childNodes = getDescendants(node, false)
    childNodes.forEach((childNode) => {
      // 判断当前子节点是否是缩放时改动缩放值
      const changeByScale = isTransformByScale(childNode)
      preNodes.push({
        uuid: childNode.uuid,
        height: childNode.height,
        width: childNode.width,
        x: childNode.x,
        y: childNode.y,
        scaleX: childNode.scaleX,
        scaleY: childNode.scaleY,
      })
      const childNodeOriginX = childNode.getWorldPosition().x
      const childNodeOriginY = childNode.getWorldPosition().y
      const childDelta = getScaleShapeDelta(
        [childNodeOriginX, childNodeOriginY],
        [preBox.originX, preBox.originY],
        node.rotation,
        [valueWidthScale, valueHeightScale],
      )
      // 为了获取节点相对于父节点的偏移量，需要先把设置节点的世界坐标
      moveNodeByWorldPosition({
        node: childNode,
        x: childNodeOriginX + childDelta[0],
        y: childNodeOriginY + childDelta[1],
      })
      nodesStyle.push({
        uuid: childNode.uuid,
        width: changeByScale
          ? childNode.width
          : childNode.width * valueWidthScale,
        height: changeByScale
          ? childNode.height
          : childNode.height * valueHeightScale,
        x: childNode.x,
        y: childNode.y,
        scaleX: changeByScale
          ? childNode.scaleX * valueWidthScale
          : childNode.scaleX,
        scaleY: changeByScale
          ? childNode.scaleY * valueHeightScale
          : childNode.scaleY,
      })
    })
  }

  return {
    style: nodesStyle as LayoutStyle[],
    oldStyle: preNodes as LayoutStyle[],
  }
}
